import React, { useContext, useEffect, useState } from 'react';
import {
  IonButton,
  IonContent,
  IonPage,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonToolbar,
  IonFooter,
} from '@ionic/react';
import {
  checkboxOutline,
  checkmark,
  document,
  create,
  download,
} from 'ionicons/icons';
import {
  getJobFlags,
  getStoredObjectKey,
  getSubmittedExpenses,
  getJob,
  getProperty,
  getBooking,
  getBookingRooms,
  getPropertyRooms,
  getBookingNotes,
  getBookingCount,
  setKey,
  updateStoredObjectKey,
  updateSelfEmployedJobStatus,
  getSelfEmployedJobsCount,
  updateJobFlags,
  removeStoredArrayElement,
  getAllSelfEmployedJobs,
  getStoredArray
} from '../utilities/Storage';
import moment from 'moment';
import {
  requestJobData, requestPropertyListById,
} from "../utilities/ApiTasks";
import { JobRow } from '../components/JobSelfEmployed/JobRow';
import { Extras } from '../components/JobSelfEmployed/Extras';
import { Rooms } from '../components/JobSelfEmployed/Rooms';
import { NoJobDetails } from '../components/JobSelfEmployed/NoJobDetails';
import {addUpdate} from "../utilities/Updates";
import { ApiContext } from '../contexts/ApiContext';
import {DownloadPropertyReport} from "../utilities/DownloadPropertyReport";
import {Toolbar} from "./Toolbar";
import {userIsSelfEmployed} from "../dataStructures/user/predicates";
import { useAuthentication } from '../authentication/authentication-hook';

export default function JobSelfEmployed({history, match}) {
  const { stackProcessing, setSelfEmployedJobsCount } = useContext(ApiContext);
  const id = match.params.id;
  const { user } = useAuthentication();
  const isSelfEmployed = userIsSelfEmployed(user);
  const [job, setJob] = useState();
  const [property, setProperty] = useState();
  const [jobFlags, setJobFlags] = useState();
  const [notes, setNotes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [showBooking, setShowBooking] = useState(false);
  const [booking, setBooking] = useState({});
  const [propertyRooms, setPropertyRooms] = useState({});
  const [bookingRooms, setBookingRooms] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [bookingName, setBookingName] = useState('');
  const [stackHasProcessed, setStackHasProcessed] = useState(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const getData = async () => {

      if (!job || job?.id !== id) {
        setJob(null);
      }

      await setKey("lastVisitedJob", id);

      if (!isMounted) return;
      await requestJobData(id);
      const existingJobData = await getJob(id);

      if(!existingJobData) {
        return null;
      }

      setJob(existingJobData);

      const propertyId = existingJobData.property.substr(existingJobData.property.lastIndexOf("/") + 1);
      await requestPropertyListById([propertyId]);
      const p = await getProperty(propertyId);
      setProperty(p);

      let bookingData = {};
      let bookingRooms = [];
      let propertyRooms = [];
      let jn = [];

      if (existingJobData?.booking?.id) {
        bookingData = await getBooking(existingJobData?.booking?.id);
        bookingRooms = await getBookingRooms(existingJobData?.booking?.id);
        propertyRooms = await getPropertyRooms(propertyId);
        jn = await getBookingNotes(existingJobData?.booking?.id);
      }

      setBooking(bookingData);
      setBookingRooms(bookingRooms);
      setPropertyRooms(propertyRooms);

      let validNotes = [];
      if(jn) {
        for (const jobNote of jn) {
          if (jobNote.note.visibletocleaner || jobNote.note.visibletokeyholder  || jobNote.note.visibletoowner) {
            let texts = [];
            for (const text of jobNote.note.notetexts) {
              texts.push(text.notetext);
            }

            validNotes.push({
              subject: jobNote.note.subject,
              text: texts.join('; ')
            });
          }
        }
      }

      setNotes(validNotes);

      let otherBookingsAtTheSameProperty = 0;
      if (bookingData.guesttype === 'Customer'
          && bookingData.customers
          && bookingData.customers.length > 0
          && bookingData.property
          && bookingData.property.id
      ) {

        const hash = [bookingData.id, bookingData.customers[0].id, bookingData.property.id].join('|');
        otherBookingsAtTheSameProperty = await getBookingCount(hash);
      }


      if (bookingData?.guesttype == "Owner") {
        setBookingName(`Owner Booking - ${existingJobData.booking.ownerbookingtype.description}`);
      } else if (bookingData?.guesttype == "Agency") {
        setBookingName(`Agency Booking - ${existingJobData.booking.ownerbookingtype.description}`);
      } else if (bookingData?.customers) {
        setBookingName(`${bookingData.customers[0].name ?? ''} - ${otherBookingsAtTheSameProperty} other bookings at the property`);
      } else {
        setBookingName('');
      }

      const jf = await getJobFlags(id);

      setJobFlags(getJobFlagsFromJob(existingJobData));

      if (Object.keys(jf).length > 0) {
        setJobFlags(jf);
      }

      let expenses = await getExpenses(id, existingJobData);
      setExpenses(expenses);
    }

    getData().then(() => {
      setIsLoading(false);
    });

    const unlisten = history.listen(val => {
      if (val.pathname === '/job/' + id) {
      }
    });

    return () => {
      isMounted = false;
      unlisten();
    };

  }, [id]);

  const getExpenses = async (id, existingJobData) => {
    const se = await getSubmittedExpenses(id);

      let expenses = se.existing || {};

      if (existingJobData.expenses) {
        for (const expense of existingJobData.expenses) {
          if (!expenses[expense.id]) {
            expenses[expense.id] = expense;
          }
        }
      }

      expenses = [...Object.values(expenses)];

      if (se.new) {
        for (const e of se.new) {
          const find = Object.values(expenses).find(expense => {
            return e.amountnetestimate == expense.amountnetestimate &&
                e.description === expense.description;
          });

          if (!find) {
            expenses.push(e);
          }
        }
      }

      return expenses;
  };

  useEffect(() => {
    if(stackProcessing) {
      setStackHasProcessed(true);
    }

    if(stackHasProcessed && !stackProcessing) {
      getJob(id).then((existingJobData) => {
        getExpenses(id, existingJobData).then((expenses) => {
          setExpenses(expenses);
          setStackHasProcessed(false);
        });
      });
    }
  }, [stackProcessing]);

  useEffect(() => {
    if (!jobFlags) {
      return;
    }

    if (jobFlags.started && jobFlags.waspaused) {
      // job has been 'paused' at some point, so show a total duration timer
      const sorted = [...jobFlags.history].sort((a, b) => moment(a.updated) > moment(b.updated) ? 1 : -1);
      let started = undefined;

      sorted.forEach((item, i) => {
        if (item.update === 'started' && !started) {
          started = moment(item.updated);
        }
        if (item.update === 'paused' && started) {
          if (sorted[i + 1]) {
            const seconds = moment(sorted[i + 1].updated).diff(moment(item.updated), 'seconds', true);
            started.add(seconds, 'seconds');
          } else {
            const seconds = moment().diff(moment(item.updated), 'seconds', true);
            started.add(seconds, 'seconds');
          }
        }
      });
    }
  }, [jobFlags]);

  const getJobFlagsFromJob = (job) => {
    let jf = {
      approved: false,
      started: false,
      paused: false,
      completed: false,
      cannotcomplete: false,
      cannotstart: false,
      waspaused: false,
      invoicerejected: false,
      invoiceapproved: false,
      history: [],
    };

    const completeStatuses = [
      'approved',
      'completed',
      'invoiced',
      'invoice approved',
      'invoice rejected',
      'owner charged',
      'supplier paid',
      'financially completed',
    ];

    const jobStatus = job.status.toLowerCase();

    if (job?.substatus?.id && job?.substatus?.workordersubstatus?.substatusreference === 'could_not_complete') {
      jf.cannotcomplete = true;
    } else if (job?.substatus?.id && job?.substatus?.workordersubstatus?.substatusreference === 'cannot_start_job') {
      jf.cannotstart = true;
    } else if (job?.substatus?.id && job?.substatus?.workordersubstatus?.substatusreference === 'started_paused') {
      jf.paused = true;
    } else if (jobStatus === 'approved') {
      jf.approved = true;
      setShowBooking(true)
    }else if (jobStatus === 'invoice approved') {
      jf.invoiceapproved = true;
      setShowBooking(true)
    } else if (jobStatus === 'started') {
      jf.started = true;
    } else if (completeStatuses.includes(jobStatus)) {
      if (jobStatus === 'invoice rejected') {
        jf.invoicerejected = true;
      }
      jf.completed = true;
    }

    return jf;
  }

  if (!job || !jobFlags || !property) {
    return <NoJobDetails isLoading={isLoading} />;
  }

  let displayStatus = "";
  switch (job.status) {
    case 'Approved':
      displayStatus = `To do`;
      break;
    case 'Completed':
      displayStatus = `${job.status} - Submit costs`;
      break;
    case 'Invoiced':
      displayStatus = `Costs submitted - awaiting approval`;
      break;
    case 'Invoice Rejected':
      displayStatus = `Costs rejected`;
      break;
    case 'Invoice Approved':
      displayStatus = `Costs approved`;
      break;
    default:
      break;
  }

  const markJobCompleted = async () => {
    setJob({...job, status: "Completed"});
    const jf = {...jobFlags, completed: true, approved: false};
    setJobFlags(jf);
    await updateJobFlags(job.id, jf);

    job.status = "Completed";
    await updateStoredObjectKey('jobs', job.id, job);
    await updateSelfEmployedJobStatus(job.id, "Completed");

    //cause a re-render of job screen
    setSelfEmployedJobsCount(await getSelfEmployedJobsCount());

    await addUpdate('job_completed', {
      jobId: job.id,
      fromdatetime: moment().format('YYYY-MM-DD HH:mm:ss'),
    });
  }

  const submitCosts = async () => {
    setJob({...job, status: "Invoice Approved"});
    const expensesIncreased = expenses.some(expense => expense.amountIncreased === true);

    const jf = {...jobFlags, completed: false, invoiceapproved: !expensesIncreased};
    setJobFlags(jf);
    await updateJobFlags(job.id, jf);

    job.status = expensesIncreased ? "Invoiced" : "Invoice Approved";
    await updateStoredObjectKey('jobs', job.id, job);

    await updateSelfEmployedJobStatus(job.id, expensesIncreased ? "Invoiced" : "Invoice Approved", "Completed");

    //cause a re-render of job screen
    setSelfEmployedJobsCount(await getSelfEmployedJobsCount());

   await addUpdate('submit_costs', {
      jobId: job.id,
      invoicedate: moment().format('YYYY-MM-DD'),
    });

    history.push(`/jobs`);
  }

  const pageTitle = `Job ${id}`;

  return (
      <IonPage>
        <IonHeader>
          <Toolbar title={pageTitle} backButtonHref="/" />
        </IonHeader>
        <IonContent>
          <IonItem color="lgrey" lines="none">
            <h5 className="ion-no-margin">Job Details</h5>
          </IonItem>
          <JobRow label={"Job ID"} value={job.id}/>
          <JobRow label={"Job Status"} value={displayStatus}/>
          <JobRow label={"Required Date"} value={job.requiredbydate || job.preferredstartdatetime ? (moment(job.requiredbydate || job.preferredstartdatetime ).format('ddd Do MMM YYYY')) : 'Date not provided'}/>
          <JobRow
              label={"Property"}
              value={`${property.id} - ${property.name}`}
              disabled={downloadInProgress}
              action={async ()=> {
                setDownloadInProgress(true);
                await DownloadPropertyReport(property.id);
                setDownloadInProgress(false);
              }}
              actionIcon={download}
          />
          <JobRow label={"Job Title"} value={job.shortdescription}/>
          <JobRow label={"Detailed Description"} value={job.fulldescription}/>

          {notes.length > 0 && (
              <>
                <IonItem color="lgrey" lines="none">
                  <h4 className="ion-no-margin">Rejected notes</h4>
                </IonItem>

                {notes.map((row, index) => (
                    <JobRow
                        key={index}
                        label={row.subject}
                        value={row.text}
                        color="pink"
                    />
                ))}
              </>
          )}

          <IonItem
              color="lgrey"
              lines="none"
              detail={jobFlags.completed || jobFlags.invoicerejected}
              detailIcon={create}
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/job/${job.id}/expenses`);
              }}
          >
            <h4 className="ion-no-margin">Job Items</h4>
          </IonItem>
          {expenses.length > 0 ? (expenses.map((row, index) => (
              row.amountnet > 0 && row.amountnet != row.amountnetestimate ?
                <JobRow
                  key={index}
                  label={row.description}
                  value={`${row.currency.plaintextsymbol} ${parseFloat(row.amountnet).toFixed(2)}`}
                  strikethroughValue={`${row.currency.plaintextsymbol} ${parseFloat(row.amountnetestimate).toFixed(2)}`}
                  otherDetails={'NET amount (VAT will be added if applicable)'}
                />
              : <JobRow
                  key={index}
                  label={row.description}
                  value={`${row.currency.plaintextsymbol} ${parseFloat(row.amountnetestimate).toFixed(2)}`}
                  otherDetails={'NET amount (VAT will be added if applicable)'}
              />
          ))) : (
              <JobRow label={"-"} value=""/>
          )}

          {booking?.id && (
            <IonItem button color="lgrey" lines="none" detail onClick={() => setShowBooking(prev => !prev)}>
              <h5 className="ion-no-margin">Booking</h5>
            </IonItem>
          )}
          {showBooking && booking?.id && (
              <>
                <JobRow label={"Booking ID"} value={booking.id}/>
                <JobRow label={"Booking Dates"} value={`${moment(booking.fromdate, 'YYYY-MM-DD').format('DD/MM/YYYY')} - ${moment(booking.todate, 'YYYY-MM-DD').format('DD/MM/YYYY')}`} />
                <JobRow label={"Booking Name"} value={bookingName} />
                <JobRow label={"Ad/Ch/Inf/Pet"} value={`${booking?.adults ?? 0}/${booking?.children ?? 0}/${booking?.infants ?? 0}/${booking?.pets ?? 0}`} />
                <Extras bookingExtras={booking.extras ?? []} />
                <Rooms bookingRooms={ bookingRooms } propertyRooms={ propertyRooms } />
              </>
          )}

          <IonItem
              button={job.checklists.length > 0}
              onClick={(e) => {
                if (!job.checklists.length > 0) return;

                e.stopPropagation();
                history.push(`/job/${id}/checklists`);
              }}
              color="lgrey"
              lines="none"
              detail={job.checklists.length}
          >

            <IonIcon icon={checkboxOutline} slot="start" />
            <h5 className="ion-no-margin">
              {job.checklists.length} checklist(s)
            </h5>
          </IonItem>
        </IonContent>
          <IonFooter>
            <IonToolbar>
              <div className="ion-padding-horizontal">
                {jobFlags.approved && moment().isAfter(moment(job.preferredstartdatetime || job.requiredbydate).subtract(2, 'days')) &&  (
                    <>
                      <IonItem color="pblue" className="ion-padding-top ion-padding-bottom" lines="none">
                        <IonLabel className="ion-text-wrap">
                          If you are unable to start or complete this job,
                          please contact the office immediately.
                        </IonLabel>
                      </IonItem>

                      <IonButton
                          expand="block"
                          color="success"
                          onClick={markJobCompleted}
                          disabled={stackProcessing}
                      >
                        <IonIcon icon={checkmark} slot="start" />
                        Mark job as completed
                      </IonButton>
                    </>
                )}

                {((jobFlags.completed && !jobFlags.invoiceapproved) || jobFlags.invoicerejected) && (
                    <IonButton
                        expand="block"
                        color="primary"
                        onClick={submitCosts}
                        disabled={stackProcessing}
                    >
                      <IonIcon icon={document} slot="start" />
                      Submit costs
                    </IonButton>
                )}
              </div>
            </IonToolbar>
          </IonFooter>
      </IonPage>
  );
}
